'use client';

import { Button, Modal } from '@/components/core';
import { UIComponent } from '@/components/core/components';
import { msg } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { useLingui } from '@lingui/react';
import React, { useState } from 'react';
import { useLocalizedLinks } from '@/components/core/hooks';
import theme from '@/components/core/theme';
import * as styles from './download-app-modal.styles';
import { Image } from '../../atoms';

const reasons = {
  bookmark: {
    title: msg`Save it on the Peoople app!`,
    description: msg`Download Peoople to save the recommendations you love. You can organize them into Public or Private Collections, so everything's at your fingertips.`,
  },
  follow: {
    title: msg`Follow them on the Peoople app!`,
    description: msg`Download Peoople to follow your friends and favorite influencers and never miss their recommendations.`,
  },
  like: {
    title: msg`Give it a like on the Peoople app!`,
    description: msg`Download Peoople to like recommendations from your friends and favorite influencers. Psst... A little secret: the likes are unlimited 🙌`,
  },
  default: {
    title: msg`Download the app to enjoy all our features`,
    description: msg`Enjoy a richer experience with enhanced features in the app.`,
  },
};

const Context = React.createContext<React.Dispatch<React.SetStateAction<keyof typeof reasons | undefined>>>(() => {});

const DownloadAppModal: UIComponent = ({ children }) => {
  const [reason, setReason] = useState<keyof typeof reasons>();
  const values = reason ? reasons[reason] : undefined;
  const { i18n } = useLingui();
  const links = useLocalizedLinks();

  return (
    <Context.Provider value={setReason}>
      {children}
      <Modal
        open={!!reason}
        onClose={() => {
          return setReason(undefined);
        }}
      >
        <Modal.Card className={styles.root}>
          {values && (
            <div className={styles.grid}>
              <div className={styles.image}>
                <picture>
                  <source media={`(min-width: ${theme.sizes.screens.md})`} srcSet={`/download-app/${reason}-desktop.png`} type="image/png" />
                  <img alt="reason" src={`/download-app/${reason}-mobile.png`} />
                </picture>
              </div>
              <div className={styles.content}>
                <div className={styles.title}>{i18n._(values.title)}</div>
                <div className={styles.description}>{i18n._(values.description)}</div>
                <Button as="a" href={links.downloadApp()} target="_blank" variant="primary" className={styles.mobileDownload}>
                  <Trans>Download</Trans>
                </Button>
                <div className={styles.desktopDownload}>
                  <p className={styles.downloadTitle}>
                    <Trans>Scan here to download the app</Trans>
                  </p>
                  <div className={styles.qr}>
                    <Image src="/download-app.svg" alt="qr code" />
                  </div>
                  <div className={styles.storeBadges}>
                    <p className={styles.downloadHereText}>
                      <Trans>Or download it here</Trans>
                    </p>
                    <a href="https://apps.apple.com/es/app/peoople/id1121257820" target="_blank" rel="nofollow noopener">
                      <Image src="/download-ios.svg" width={150} alt="ios download" />
                    </a>
                    <a href="https://play.google.com/store/apps/details?id=com.peopleitrust.pitapp" target="_blank" rel="nofollow noopener">
                      <Image src="/download-android.png" width={150} alt="android download" />
                    </a>
                  </div>
                </div>
              </div>
            </div>
          )}
        </Modal.Card>
      </Modal>
    </Context.Provider>
  );
};

export const useDownloadApp = () => {
  return React.useContext(Context);
};

export default DownloadAppModal;
